import React, { useEffect, useRef, useState } from 'react';

import { useLocation, useNavigate } from 'react-router-dom';

import { useInitializedFlags } from '@liscio/common';

import { DashboardMobileView } from './DashboardMobileView';
import { DashboardDesktopView } from '../DashboardDesktopView/DashboardDesktopView';
import { useIsDesktopView } from 'custom-hooks';
import {
  consumeGuidedFileResume,
  type GuidedFileResumeState,
} from 'modules/dashboard/guidedFileIntelligence';
import {
  consumePostTermsDashboardEntry,
  isPostTermsDashboardEntry,
} from 'modules/dashboard/postTermsDashboard';

export interface DashboardViewProps {}

/**
 * Dashboard entry point. Chooses the dashboard view based on viewport + flag.
 *
 * - `enableDashboardRedesign` ON + desktop viewport -> new desktop dashboard
 * - otherwise -> existing mobile dashboard
 *
 * Note: desktop users only reach liscio-frontend at all once the matching
 * desktop-redirect exclusion for `/dashboard/*` is enabled (DesktopSiteRedirect),
 * so until then the flag effectively only affects the few desktop entry points
 * that aren't redirected to legacy.
 */
export const DashboardView: React.FC<DashboardViewProps> = () => {
  const { flags, isInitialized } = useInitializedFlags();
  const isDesktopView = useIsDesktopView();
  const location = useLocation();
  const navigate = useNavigate();
  const consumedRef = useRef(false);
  const resumeConsumedRef = useRef(false);
  const [autoOpenFileIntelligence, setAutoOpenFileIntelligence] =
    useState(false);
  const [resumedFileIntelligence, setResumedFileIntelligence] =
    useState<GuidedFileResumeState>();

  const showDesktopDashboard =
    Boolean(flags.enableDashboardRedesign) && isDesktopView;

  useEffect(() => {
    if (!isInitialized || !showDesktopDashboard || resumeConsumedRef.current)
      return;
    resumeConsumedRef.current = true;
    const resume = consumeGuidedFileResume();
    if (!resume) return;
    setResumedFileIntelligence(resume);
    setAutoOpenFileIntelligence(true);
  }, [isInitialized, showDesktopDashboard]);

  useEffect(() => {
    if (!isInitialized || consumedRef.current) return;
    if (!isPostTermsDashboardEntry(location.search)) return;
    if (!showDesktopDashboard) return;
    consumedRef.current = true;

    navigate(
      {
        pathname: location.pathname,
        search: consumePostTermsDashboardEntry(location.search),
        hash: location.hash,
      },
      { replace: true }
    );
    setAutoOpenFileIntelligence(true);
  }, [
    isInitialized,
    location.hash,
    location.pathname,
    location.search,
    navigate,
    showDesktopDashboard,
  ]);

  return showDesktopDashboard ? (
    <DashboardDesktopView
      autoOpenFileIntelligence={autoOpenFileIntelligence}
      onAutoOpenConsumed={() => setAutoOpenFileIntelligence(false)}
      resumedFileIntelligence={resumedFileIntelligence}
    />
  ) : (
    <DashboardMobileView />
  );
};

export default DashboardView;
